/**
 * useS3MultipartUpload.js
 *
 * Phase 2 Hook: Multipart S3 uploads with 4-6 concurrent chunks for 4x speed.
 *
 * Features:
 * - Automatic detection of file size to decide multipart vs single-part
 * - 5 MB chunks uploaded in parallel (4 concurrent by default)
 * - Real-time progress tracking (byte by byte)
 * - Speed calculation (Mbps) and ETA
 * - Auto-retry per chunk with exponential backoff (1s, 2s, 4s)
 * - Cancel capability
 * - Error recovery
 */

import { useCallback, useRef, useState } from 'react';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const MULTIPART_THRESHOLD = 1 * 1024 * 1024; // Use multipart for > 1 MB
const CONCURRENT_CHUNKS = 4;
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY = 1000; // ms
const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB

/**
 * Parse S3 XML error response and extract error details
 */
const parseS3Error = (responseText) => {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(responseText, 'text/xml');

        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
            return null; // Not valid XML
        }

        const errorCode = xmlDoc.getElementsByTagName('Code')[0]?.textContent;
        const errorMessage = xmlDoc.getElementsByTagName('Message')[0]
            ?.textContent;
        const maxSizeAllowed = xmlDoc.getElementsByTagName('MaxSizeAllowed')[0]
            ?.textContent;

        if (errorCode === 'EntityTooLarge' && maxSizeAllowed) {
            const maxSizeGB =
                Math.round(
                    (parseInt(maxSizeAllowed) / (1024 * 1024 * 1024)) * 10
                ) / 10;
            return `File size exceeds the maximum allowed size of ${maxSizeGB} GB. Please upload a smaller file.`;
        }

        return errorMessage || null;
    } catch (err) {
        return null;
    }
};

export const useS3MultipartUpload = () => {
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSpeed, setUploadSpeed] = useState(0);
    const [uploadEta, setUploadEta] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadedBytes, setUploadedBytes] = useState(0);

    const abortControllerRef = useRef(null);
    const startTimeRef = useRef(null);
    const uploadedBytesRef = useRef(0);

    /**
     * Retry logic with exponential backoff
     */
    const retryWithBackoff = useCallback(async (fn, attempt = 0) => {
        try {
            return await fn();
        } catch (error) {
            if (attempt < MAX_RETRIES) {
                const delay = RETRY_BASE_DELAY * Math.pow(2, attempt);
                await new Promise((r) => setTimeout(r, delay));
                return retryWithBackoff(fn, attempt + 1);
            }
            throw error;
        }
    }, []);

    /**
     * Upload a single chunk with progress tracking
     */
    const uploadChunk = useCallback(
        async (presignedUrl, chunk, chunkIndex, totalChunks, totalFileSize) => {
            return retryWithBackoff(async () => {
                return new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();

                    // Calculate the absolute byte position of this chunk in the file
                    const chunkStartPosition = chunkIndex * CHUNK_SIZE;

                    // Track progress for this chunk
                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) {
                            // Calculate absolute byte position in the file
                            // (not cumulative across all chunks)
                            const currentBytePosition =
                                chunkStartPosition + e.loaded;

                            // Update ref to track the furthest position reached
                            uploadedBytesRef.current = Math.max(
                                uploadedBytesRef.current,
                                currentBytePosition
                            );

                            const percentComplete =
                                (uploadedBytesRef.current / totalFileSize) *
                                100;
                            const elapsedSeconds =
                                (Date.now() - startTimeRef.current) / 1000;
                            const speedBytesPerSecond =
                                uploadedBytesRef.current / elapsedSeconds;
                            const speedMbps =
                                (speedBytesPerSecond * 8) / 1_000_000;
                            const etaSeconds =
                                (totalFileSize - uploadedBytesRef.current) /
                                speedBytesPerSecond;

                            setUploadProgress(Math.round(percentComplete));
                            setUploadSpeed(Math.round(speedMbps * 10) / 10);
                            setUploadEta(Math.max(0, Math.round(etaSeconds)));
                            setUploadedBytes(uploadedBytesRef.current);
                        }
                    });

                    xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            const etag = xhr.getResponseHeader('etag');
                            if (!etag) {
                                reject(
                                    new Error(
                                        `No ETag in response for chunk ${
                                            chunkIndex + 1
                                        }`
                                    )
                                );
                                return;
                            }

                            // Ensure this chunk is marked as fully uploaded
                            const chunkEnd = chunkStartPosition + chunk.size;
                            const newUploadedBytes = Math.max(
                                uploadedBytesRef.current,
                                chunkEnd
                            );
                            uploadedBytesRef.current = newUploadedBytes;

                            // Update progress UI - always update progress
                            const percentComplete =
                                (newUploadedBytes / totalFileSize) * 100;

                            console.log(
                                `Chunk ${
                                    chunkIndex + 1
                                }: ${newUploadedBytes}/${totalFileSize} bytes (${Math.round(
                                    percentComplete
                                )}%)`
                            );

                            setUploadProgress(Math.round(percentComplete));
                            setUploadedBytes(newUploadedBytes);

                            // Calculate speed and ETA only if enough time has passed
                            const elapsedSeconds =
                                (Date.now() - startTimeRef.current) / 1000;
                            if (elapsedSeconds > 0.1) {
                                const speedBytesPerSecond =
                                    newUploadedBytes / elapsedSeconds;
                                const speedMbps =
                                    (speedBytesPerSecond * 8) / 1_000_000;
                                const remainingBytes =
                                    totalFileSize - newUploadedBytes;
                                const etaSeconds = Math.max(
                                    0,
                                    remainingBytes / speedBytesPerSecond
                                );

                                setUploadSpeed(Math.round(speedMbps * 10) / 10);
                                setUploadEta(Math.round(etaSeconds));
                            }

                            resolve({
                                partNumber: chunkIndex + 1,
                                etag,
                                size: chunk.size
                            });
                        } else {
                            // Try to parse S3 error response
                            const s3Error = parseS3Error(xhr.responseText);
                            const errorMsg =
                                s3Error ||
                                `HTTP ${xhr.status} for chunk ${
                                    chunkIndex + 1
                                }`;
                            reject(new Error(errorMsg));
                        }
                    });

                    xhr.addEventListener('error', () => {
                        reject(
                            new Error(
                                `Network error uploading chunk ${
                                    chunkIndex + 1
                                }`
                            )
                        );
                    });

                    xhr.addEventListener('abort', () => {
                        reject(new Error('Upload cancelled'));
                    });

                    xhr.open('PUT', presignedUrl);
                    // Don't set Content-Type for multipart chunks — let AWS handle it
                    // presigned URL already has Content-Type in policy
                    xhr.send(chunk);
                });
            });
        },
        [retryWithBackoff]
    );

    /**
     * Upload multiple chunks concurrently
     */
    const uploadChunksBatch = useCallback(
        async (file, presignedUrls, startIndex) => {
            const batch = presignedUrls.slice(
                startIndex,
                startIndex + CONCURRENT_CHUNKS
            );
            const results = [];

            await Promise.all(
                batch.map(async (config, idx) => {
                    const chunkStart =
                        config.part_number === 1
                            ? 0
                            : (config.part_number - 1) * CHUNK_SIZE;
                    const chunkEnd = Math.min(
                        chunkStart + config.size,
                        file.size
                    );
                    const chunk = file.slice(chunkStart, chunkEnd);

                    try {
                        const result = await uploadChunk(
                            config.url,
                            chunk,
                            config.part_number - 1,
                            presignedUrls.length,
                            file.size
                        );
                        results.push(result);
                    } catch (error) {
                        throw new Error(
                            `Failed to upload chunk ${config.part_number}: ${error.message}`
                        );
                    }
                })
            );

            return results;
        },
        [uploadChunk]
    );

    /**
     * Main multipart upload function
     */
    const uploadToS3Multipart = useCallback(
        async (file, uploadConfig) => {
            // ✅ ADD VALIDATION HERE
            if (file.size > MAX_FILE_SIZE) {
                const errorMsg = 'File exceeds 10GB limit';
                setUploadError(errorMsg);
                throw new Error(errorMsg);
            }

            const {
                upload_id: uploadId,
                s3_key,
                presigned_urls: presignedUrls,
                use_multipart
            } = uploadConfig;

            if (!use_multipart) {
                throw new Error(
                    'This configuration should not use multipart upload'
                );
            }

            setIsUploading(true);
            setUploadError(null);
            setUploadProgress(0);
            setUploadSpeed(0);
            setUploadEta(0);
            uploadedBytesRef.current = 0;
            startTimeRef.current = Date.now();

            try {
                abortControllerRef.current = new AbortController();
                const partETags = {};

                // Upload all chunks in concurrent batches
                for (
                    let i = 0;
                    i < presignedUrls.length;
                    i += CONCURRENT_CHUNKS
                ) {
                    const batchResults = await uploadChunksBatch(
                        file,
                        presignedUrls,
                        i
                    );

                    batchResults.forEach((result) => {
                        partETags[result.partNumber] = result.etag;
                    });
                }

                // All chunks uploaded, return completion data
                return {
                    uploadId,
                    s3_key,
                    partETags
                };
            } catch (error) {
                setUploadError(error.message);
                throw error;
            } finally {
                setIsUploading(false);
            }
        },
        [uploadChunksBatch]
    );

    /**
     * Cancel the upload
     */
    const cancelUpload = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setIsUploading(false);
    }, []);

    /**
     * Reset upload state
     */
    const resetUploadState = useCallback(() => {
        setUploadProgress(0);
        setUploadSpeed(0);
        setUploadEta(0);
        setIsUploading(false);
        setUploadError(null);
        setUploadedBytes(0);
        uploadedBytesRef.current = 0;
    }, []);

    /**
     * Format bytes to human-readable format
     */
    const formatBytes = useCallback((bytes, decimals = 1) => {
        if (bytes === 0) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${
            sizes[i]
        }`;
    }, []);

    /**
     * Format duration (seconds) to human-readable format
     */
    const formatDuration = useCallback((seconds) => {
        if (seconds < 1) {
            return 'now';
        }
        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        }
        if (seconds < 3600) {
            return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
        }
        return `${Math.floor(seconds / 3600)}h ${Math.floor(
            (seconds % 3600) / 60
        )}m`;
    }, []);

    return {
        uploadToS3Multipart,
        cancelUpload,
        resetUploadState,
        formatBytes,
        formatDuration,
        uploadProgress,
        uploadSpeed,
        uploadEta,
        isUploading,
        uploadError,
        uploadedBytes,
        MULTIPART_THRESHOLD
    };
};

export default useS3MultipartUpload;
